import { readingOrder } from '../../shared/frameOrder'
import type { Frame } from '../../shared/types'
import { navigate } from '../App'
import { downloadFrameExport, downloadFramesPdf, type FrameExportFormat } from '../lib/frameExport'
import { sendFramesToCanva, useCanvaStatus } from '../integrations/canva/export'
import { Button } from './ui/button'
import { Modal, ModalActions, ModalLede, ModalTitle } from './ui/modal'
import { useT } from '@/lib/i18n'

export function FrameExportModal({
  frame,
  groupFrames = [frame],
  onClose,
  onSendToFigma,
  figmaFrameCount,
}: {
  frame: Frame
  /** the selection the menu acts on; the PDF binds all of it, a page per
   *  frame in reading order */
  groupFrames?: Frame[]
  onClose: () => void
  onSendToFigma: () => void
  figmaFrameCount: number
}) {
  const canva = useCanvaStatus()
  const t = useT()

  function download(format: FrameExportFormat) {
    void downloadFrameExport(frame, format)
    onClose()
  }

  function downloadPdf() {
    void downloadFramesPdf(frame.canvasId, readingOrder(groupFrames))
    onClose()
  }

  function openIntegrations() {
    onClose()
    navigate('/integrations')
  }

  return (
    <Modal size="sm" onClose={onClose}>
      <ModalTitle className="break-words">{t('panels.frameExportTitle', { name: frame.name })}</ModalTitle>
      <ModalLede>{canva?.enabled === false ? t('hosted.frameExportLedeFigma') : t('hosted.frameExportLede')}</ModalLede>
      <div className="mt-5 grid gap-2">
        <Button variant="ghost" className="justify-start" onClick={() => download('png')}>
          {t('panels.frameExportPng')}
        </Button>
        <Button variant="ghost" className="justify-start" onClick={() => download('jpg')}>
          {t('panels.frameExportJpg')}
        </Button>
        <Button
          variant="ghost"
          className="h-auto flex-col items-start gap-1 whitespace-normal text-left"
          onClick={() => download('psd')}
        >
          <span>{t('hosted.frameExportPsd')}</span>
          <span className="text-xs font-normal text-ink-soft">{t('hosted.frameExportPsdHint')}</span>
        </Button>
        <Button
          variant="ghost"
          className="h-auto flex-col items-start gap-1 whitespace-normal text-left"
          onClick={downloadPdf}
        >
          <span>
            {groupFrames.length > 1
              ? t('panels.frameExportPdfGroup', { count: groupFrames.length })
              : t('panels.exportDownloadPdf')}
          </span>
          <span className="text-xs font-normal text-ink-soft">
            {groupFrames.length > 1 ? t('panels.frameExportPdfGroupHint') : t('panels.frameExportPdfHint')}
          </span>
        </Button>
        {canva?.connected ? (
          <Button
            variant="ghost"
            className="justify-start"
            onClick={() => {
              void sendFramesToCanva([frame])
              onClose()
            }}
          >
            {t('hosted.exportCanvaTitle')}
          </Button>
        ) : (
          canva?.enabled !== false && (
            /* not a disabled Button: the Integrations link inside must stay
               clickable, and a button can't hold another button */
            <div
              aria-disabled
              className="grid gap-1 rounded-md border border-line px-3.5 py-2 text-[13px] font-medium text-ink"
            >
              <span className="opacity-45">{t('hosted.exportCanvaTitle')}</span>
              {canva && (
                <span className="text-xs font-normal text-ink-soft">
                  {t('hosted.frameExportCanvaConnectBefore')}{' '}
                  <button
                    type="button"
                    className="font-semibold text-ink underline underline-offset-2 hover:text-accent-ink"
                    onClick={openIntegrations}
                  >
                    {t('hosted.integrations')}
                  </button>{' '}
                  {t('hosted.frameExportCanvaConnectAfter')}
                </span>
              )}
            </div>
          )
        )}
        <Button variant="ghost" className="justify-start" onClick={onSendToFigma}>
          {figmaFrameCount > 1 ? t('hosted.sendFramesToFigma', { count: figmaFrameCount }) : t('hosted.sendToFigma')}
        </Button>
      </div>
      <ModalActions>
        <Button variant="ghost" onClick={onClose}>
          {t('common.cancel')}
        </Button>
      </ModalActions>
    </Modal>
  )
}
